import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

type Toast = { id: number; message: string; tone: 'ok' | 'error' }

const ToastContext = createContext<{
  notify: (message: string, tone?: 'ok' | 'error') => void
} | null>(null)

let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const notify = useCallback((message: string, tone: 'ok' | 'error' = 'ok') => {
    const id = nextId++
    setToasts((current) => [...current, { id, message, tone }])
    window.setTimeout(() => {
      setToasts((current) => current.filter((t) => t.id !== id))
    }, tone === 'error' ? 8000 : 4000)
  }, [])

  const value = useMemo(() => ({ notify }), [notify])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={toast.tone === 'error' ? 'toast toast--error' : 'toast'}>
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside <ToastProvider>')
  return context
}

/** Run a mutation, surface success or the API's message, and refresh a view. */
export function useAction() {
  const { notify } = useToast()
  return useCallback(
    async (run: () => Promise<unknown>, success: string, after?: () => void) => {
      try {
        await run()
        notify(success)
        after?.()
      } catch (caught) {
        notify(caught instanceof Error ? caught.message : String(caught), 'error')
      }
    },
    [notify],
  )
}
